/**
 * Геолокация. Только разовый снимок позиции — фонового трекинга нет
 * и не должно появиться без отдельного согласия пользователя.
 *
 * ── Почему нужен кэш последней позиции ────────────────────────────
 *
 * Машины выдают в подземных паркингах, где сигнал гаснет о бетон.
 * Свежего фикса там не будет никогда, и выбор стоит не между точной
 * и неточной координатой, а между координатой въезда в гараж
 * и полным отсутствием данных. Первая отвечает на вопрос «в каком
 * здании взяли машину», второе не отвечает ни на что.
 *
 * Поэтому кэш есть, но он честно помечается источником `cached`:
 * администратор должен видеть, что точка не свежая, а не гадать,
 * почему водитель «был» на въезде, когда машина уже уехала.
 */
import * as storage from './storage'

const TIMEOUT_MS = 10_000

/** Ключ последней известной позиции. */
const CACHE_KEY = 'geo:last'

/**
 * Сколько живёт кэш. Полчаса — это про смену места, а не про точность:
 * координата получасовой давности ещё описывает тот же паркинг,
 * вчерашняя не описывает ничего.
 */
const CACHE_TTL_MS = 30 * 60 * 1000

/** @returns {Promise<{lat:number,lng:number,accuracy:number,source:'gps'|'network'|'none'}>} */
export function getCurrentPosition() {
  return new Promise((resolve) => {
    const none = { lat: null, lng: null, accuracy: null, source: 'none' }
    if (!globalThis.navigator?.geolocation) return resolve(none)

    globalThis.navigator.geolocation.getCurrentPosition(
      (pos) =>
        resolve({
          lat: pos.coords.latitude,
          lng: pos.coords.longitude,
          accuracy: pos.coords.accuracy,
          source: pos.coords.accuracy <= 50 ? 'gps' : 'network',
        }),
      // Отказ или таймаут не должны блокировать ни один сценарий.
      () => resolve(none),
      { enableHighAccuracy: true, timeout: TIMEOUT_MS, maximumAge: 0 }
    )
  })
}

/** Запомнить свежий фикс как последнюю известную позицию. */
function remember(position) {
  if (position.source === 'none') return
  try {
    storage.set(CACHE_KEY, { ...position, at: Date.now() })
  } catch {
    // Переполненное хранилище не повод ронять приёмку.
  }
}

/** Последняя известная позиция, если она ещё не протухла. */
export function lastKnown(now = Date.now()) {
  let cached = null
  try {
    cached = storage.get(CACHE_KEY)
  } catch {
    return null
  }
  if (!cached || typeof cached.lat !== 'number') return null
  if (now - (cached.at || 0) > CACHE_TTL_MS) return null

  return {
    lat: cached.lat,
    lng: cached.lng,
    accuracy: cached.accuracy,
    // Источник подменяется намеренно: точка та же, но свежесть другая,
    // и в акте она должна выглядеть иначе, чем снятая сейчас.
    source: 'cached',
  }
}

/**
 * Позиция с деградацией: свежий фикс → последняя известная → ничего.
 *
 * Возвращает всегда, никогда не бросает. Отсутствие сигнала —
 * это `source: 'none'`, а не ошибка: приёмка не должна падать
 * из-за перекрытия над головой.
 */
export async function getPositionSmart() {
  const fresh = await getCurrentPosition()
  if (fresh.source !== 'none') {
    remember(fresh)
    return fresh
  }
  return lastKnown() || fresh
}

/**
 * Подписка на уход экрана в фон и возврат.
 *
 * Живёт здесь, а не в хуке, потому что это обращение к браузеру:
 * экранам и хукам водительского приложения оно запрещено, и запрет
 * не про синтаксис, а про то, чтобы нативная сборка подменяла
 * реализацию в одном месте.
 *
 * Определение позиции браузер в фоне всё равно глушит, так что
 * знать о переходе обязан именно этот модуль.
 *
 * @returns {() => void} отписка
 */
export function onVisibilityChange(handler) {
  const doc = globalThis.document
  if (!doc?.addEventListener) return () => {}

  const listener = () => handler(!doc.hidden)
  doc.addEventListener('visibilitychange', listener)
  return () => doc.removeEventListener('visibilitychange', listener)
}

// ── Трекер ───────────────────────────────────────────────────────────────────

/** Реже — незачем: ответ «где машина» с такой частотой не меняется. */
const MIN_INTERVAL_MS = 60_000

/** Ближе — то же место. Порог отсекает дрожание координаты на стоянке. */
const MIN_DISTANCE_M = 200

const EARTH_RADIUS_M = 6_371_000

/** Расстояние по земной поверхности, метры. */
export function distanceMeters(a, b) {
  const toRad = (deg) => (deg * Math.PI) / 180
  const dLat = toRad(b.lat - a.lat)
  const dLng = toRad(b.lng - a.lng)
  const lat1 = toRad(a.lat)
  const lat2 = toRad(b.lat)

  const h =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) ** 2
  return 2 * EARTH_RADIUS_M * Math.asin(Math.min(1, Math.sqrt(h)))
}

/**
 * Следить за перемещением и отдавать точки с прореживанием.
 *
 * ── Оба условия сразу, а не любое из них ──────────────────────────
 *
 * Точка отдаётся, когда прошла минута **и** машина уехала на 200 метров.
 * Стоящая в пробке машина не производит точек вовсе — и это правильно:
 * шестьдесят одинаковых координат за час не добавляют знания, зато
 * добавляют строк в таблицу, которую потом хранить и удалять.
 *
 * ── Первая точка отдаётся сразу ───────────────────────────────────
 *
 * Иначе короткая поездка не оставила бы следа вообще: пороги
 * рассчитаны на прореживание потока, а не на отказ от начала.
 *
 * ── Фон не отслеживается, и это не наш выбор ──────────────────────
 *
 * Браузер сам глушит watchPosition, когда экран гаснет или вкладка
 * уходит в фон. Разрывы в треке — поведение платформы; трек годится
 * для подтверждения точек выдачи и возврата, но не для маршрута.
 *
 * @returns {() => void} остановка
 */
export function startTracker({ onPoint, minIntervalMs = MIN_INTERVAL_MS,
                               minDistanceM = MIN_DISTANCE_M } = {}) {
  const nav = globalThis.navigator?.geolocation
  if (!nav?.watchPosition) return () => {}

  let last = null

  const watchId = nav.watchPosition(
    (pos) => {
      const point = {
        lat: pos.coords.latitude,
        lng: pos.coords.longitude,
        accuracy: pos.coords.accuracy,
        source: pos.coords.accuracy <= 50 ? 'gps' : 'network',
        at: Date.now(),
      }

      if (last) {
        const enoughTime = point.at - last.at >= minIntervalMs
        const enoughDistance = distanceMeters(last, point) >= minDistanceM
        if (!enoughTime || !enoughDistance) return
      }

      last = point
      remember(point)
      onPoint?.(point)
    },
    // Ошибка слежения — это отсутствие точек, а не сбой поездки.
    () => {},
    { enableHighAccuracy: true, maximumAge: 0 }
  )

  return () => nav.clearWatch?.(watchId)
}
